import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "bv_compare_colleges";
const MAX_COMPARE = 4;

const CompareContext = createContext(null);

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CompareProvider = ({ children }) => {
  const [selected, setSelected] = useState(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    } catch {
      // Ignore storage errors (private browsing, quota, etc.) — selection just won't persist.
    }
  }, [selected]);

  const isSelected = useCallback(
    (id) => selected.some((c) => String(c.id) === String(id)),
    [selected]
  );

  const addToCompare = useCallback((college) => {
    if (!college?.id) return;
    setSelected((prev) => {
      if (prev.some((c) => String(c.id) === String(college.id))) return prev;
      if (prev.length >= MAX_COMPARE) return prev;
      return [
        ...prev,
        {
          id: college.id,
          collegeName: college.collegeName,
          city: college.city,
          collegeLogo: college.collegeLogo,
        },
      ];
    });
  }, []);

  const removeFromCompare = useCallback((id) => {
    setSelected((prev) => prev.filter((c) => String(c.id) !== String(id)));
  }, []);

  const clearCompare = useCallback(() => setSelected([]), []);

  const value = useMemo(
    () => ({
      selected,
      isSelected,
      addToCompare,
      removeFromCompare,
      clearCompare,
      isFull: selected.length >= MAX_COMPARE,
      maxCompare: MAX_COMPARE,
    }),
    [selected, isSelected, addToCompare, removeFromCompare, clearCompare]
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used within a CompareProvider");
  return ctx;
};
